import { z } from "zod";
import { LocaleSchema } from "./primitives.js";
/**
 * Ingredient-resolution endpoint (T04a / ADR-013 Decision 1). When Amy's
 * `compose_recipe` output (schemas/tools.ts) references an ingredient name
 * that does not yet resolve to a canonical registry ID, the caller resolves
 * it here: an immediate registry match, an enqueue into the sourced
 * ingestion pipeline (fail-closed — new ingredients only enter through that
 * pipeline, never invented inline), or an honest "unresolvable" outcome.
 * This endpoint never itself returns nutrition/allergen data — only IDs.
 */
// Architecture-review fix: the endpoint originally only accepted one name
// per call (Amy's resolve_ingredients tool and the S-3 free-text resolver
// each did an O(N) serial-request loop over it). It now ALSO accepts a
// batch shape, `{ names: string[] }`, so a caller can resolve up to 30
// names in a single round trip. The single-name shape is kept byte-for-
// byte compatible — a deployed client sending `{ name, locale }` keeps
// working unchanged — via a union rather than a breaking replacement.
export const IngredientResolveSingleRequestSchema = z.object({
    name: z.string().min(1),
    /** Optional — matching checks DE + EN names + aliases regardless (a bare `{name}` 500'd live 2026-08-25). */
    locale: LocaleSchema.optional(),
});
export const IngredientResolveBatchRequestSchema = z.object({
    names: z.array(z.string().min(1)).min(1).max(30),
    /** Unused server-side today (matching already checks both de/en names + aliases) — optional so existing single-name callers aren't forced to add it. */
    locale: LocaleSchema.optional(),
});
export const IngredientResolveRequestSchema = z.union([
    IngredientResolveSingleRequestSchema,
    IngredientResolveBatchRequestSchema,
]);
export const IngredientResolveStatusSchema = z.enum(["matched", "enqueued", "unresolvable"]);
export const IngredientResolveCandidateSchema = z.object({
    ingredientId: z.string(),
    name: z.string(),
    confidence: z.number().min(0).max(1),
});
export const IngredientResolveSingleResponseSchema = z
    .object({
    status: IngredientResolveStatusSchema,
    /** Non-null only when status is 'matched'. */
    ingredientId: z.string().nullable(),
    candidates: z.array(IngredientResolveCandidateSchema),
})
    .superRefine((val, ctx) => {
    if (val.status === "matched" && val.ingredientId === null) {
        ctx.addIssue({
            code: "custom",
            path: ["ingredientId"],
            message: "ingredientId must be non-null when status is 'matched'",
        });
    }
    if (val.status !== "matched" && val.ingredientId !== null) {
        ctx.addIssue({
            code: "custom",
            path: ["ingredientId"],
            message: "ingredientId must be null unless status is 'matched'",
        });
    }
});
/** One matched name out of a batch resolve — `names`/`units` are the registry row's own bilingual names and canonical unit (never invented; omitted is never fabricated as an empty guess). */
export const IngredientResolveBatchEntrySchema = z.object({
    name: z.string(),
    ingredientId: z.string(),
    names: z.record(z.string(), z.string()).optional(),
    units: z.string().optional(),
});
export const IngredientResolveBatchResponseSchema = z.object({
    resolved: z.array(IngredientResolveBatchEntrySchema),
    unresolved: z.array(z.string()),
});
export const IngredientResolveResponseSchema = z.union([
    IngredientResolveSingleResponseSchema,
    IngredientResolveBatchResponseSchema,
]);
//# sourceMappingURL=ingredients.js.map